<template>
    <p-button
        v-bind="$attrs"
        class="swatch"
        text
        :disabled="props.disabled"
        :style="{ backgroundColor: props.colour, height: props.size + 'px', width: props.size + 'px' }"
        @click="openPopover"
    />
    <popover ref="colourOverlay" close-on-escape>
        <tabs class="search-tabs" v-model:value="activeTabIndex">
            <tab-list>
                <tab :value="tab.value" v-for="tab in TAB_MENU_OPTIONS" :key="tab.key">
                    {{ tab.label }}
                </tab>
            </tab-list>
        </tabs>
        <div class="background">
            <div class="card" :id="props.inputId">
                <template v-if="activeTabIndex === ColourPickerTab.Presets">
                    <div class="colour-selector">
                        <p-button
                            v-for="colour in colourOptions"
                            :key="colour"
                            class="swatch"
                            text
                            :style="{ backgroundColor: colour }"
                            @click="updateValue(colour)"
                        />
                    </div>
                </template>
                <template v-if="activeTabIndex === ColourPickerTab.Custom">
                    <div class="flex-column">
                        <color-picker v-model="selectedColour" inline :input-id="props.inputId" />
                        <p-button @click="updateValue('#' + selectedColour)">Submit</p-button>
                    </div>
                </template>
            </div>
        </div>
    </popover>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import PButton from 'primevue/button';
import ColorPicker from 'primevue/colorpicker';
import Popover from 'primevue/popover';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import cssVariables from '@/assets/cssVariables.json';
import { ColourPickerTab } from '@/enums/ColourPickerTab.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import Colour from 'color';

defineOptions({
    inheritAttrs: false,
});

const TAB_MENU_OPTIONS = [
    { label: 'Presets', key: 0, value: ColourPickerTab.Presets },
    { label: 'Custom', key: 1, value: ColourPickerTab.Custom },
];

const dataStore = useDataStore();

const selectedColour = defineModel<string>();

const props = defineProps<{
    inputId?: string;
    colour: string;
    size?: number;
    rgb?: boolean;
    disabled?: boolean;
}>();

const emit = defineEmits(['update:colour']);

const colourOverlay = ref<InstanceType<typeof Popover> | null>(null);
const activeTabIndex = ref<ColourPickerTab>(ColourPickerTab.Presets);

const colourOptions = computed<Array<string>>(() => [
    ...new Set([
        ...Object.values(cssVariables.lcpColors),
        ...Object.values(cssVariables.lcpColorsSecondary),
        ...dataStore.staticStore.colours.flat(),
    ]),
]);

function openPopover (event: MouseEvent): void {
    if (!colourOverlay.value) return;
    colourOverlay.value.toggle(event);
}

function updateValue (colour: string): void {
    if (!colourOverlay.value) return;
    emit('update:colour', props.rgb ? Colour(colour).rgb().string() : colour);
    colourOverlay.value.hide();
}

</script>

<style lang="scss" scoped>

:deep(.p-tabmenu-nav) {
    background-color: var(--neutral0);
}

.card {
    background-color: var(--neutral0);
    padding: var(--smallSpacing) var(--defaultSpacing);
    border-radius: var(--borderRadiusOuter);
    color: var(--primaryFont);
}

.background {
    padding: var(--defaultSpacing) 0;
}

.colour-selector {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--defaultSpacing);
    width: max-content;
    max-height: 20rem;
    overflow-y: auto;
    padding: 0 var(--smallSpacing);
}

.flex-column {
    display: flex;
    flex-direction: column;
    gap: var(--smallSpacing);

    button {
        width: fit-content;
    }
}

.swatch {
    border: solid 1px var(--neutral400);
    border-radius: var(--defaultSpacing);
    height: 3rem;
    width: 3rem;

    &.disabled {
        pointer-events: none;
    }
}

</style>
